import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Archive, ArrowUp } from "lucide-react";

export default function GuildVault({ guild, ch, setCh, refresh }) {
  const [depositAmt, setDepositAmt] = useState(50);
  const [withdrawAmt, setWithdrawAmt] = useState(50);
  const [selectedItem, setSelectedItem] = useState("");
  const [busy, setBusy] = useState(false);

  const vault = guild.vault || { gold: 0, items: [] };
  const isLeader = guild.leader_id === ch.user_id;
  const upgradeCost = guild.level * 1000;

  const deposit = async () => {
    setBusy(true);
    try {
      const res = await api.vaultDepositGold(depositAmt);
      setCh(res.character);
      await refresh();
      toast.success(`Deposited ${depositAmt}g`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    setBusy(true);
    try {
      const res = await api.vaultWithdrawGold(withdrawAmt);
      setCh(res.character);
      await refresh();
      toast.success(`Withdrew ${withdrawAmt}g`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const depositItem = async () => {
    if (!selectedItem) return;
    setBusy(true);
    try {
      const res = await api.vaultDepositItem(selectedItem);
      setCh(res.character);
      setSelectedItem("");
      await refresh();
      toast.success("Item deposited");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const withdrawItem = async (id) => {
    setBusy(true);
    try {
      const res = await api.vaultWithdrawItem(id);
      setCh(res.character);
      await refresh();
      toast.success("Item taken");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const upgrade = async () => {
    setBusy(true);
    try {
      await api.guildUpgrade();
      await refresh();
      const c = await api.character();
      setCh(c);
      toast.success("Guild upgraded!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="panel p-3 space-y-3" data-testid="guild-vault">
      <div className="flex items-center justify-between">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
          <Archive className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} /> Guild Vault
        </div>
        <div className="font-mono text-sm text-[#E5C34A] flex items-center gap-1">
          <Coins className="w-3 h-3" strokeWidth={1.5} /> {vault.gold}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <input
            data-testid="vault-deposit-amount"
            type="number"
            min={1}
            value={depositAmt}
            onChange={(e) => setDepositAmt(parseInt(e.target.value) || 0)}
            className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1 text-sm text-[#E2DFD8]"
          />
          <button
            data-testid="vault-deposit"
            onClick={deposit}
            disabled={busy || depositAmt <= 0 || ch.gold < depositAmt}
            className="w-full py-1 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30"
          >
            Deposit
          </button>
        </div>
        <div className="space-y-1">
          <input
            data-testid="vault-withdraw-amount"
            type="number"
            min={1}
            value={withdrawAmt}
            onChange={(e) => setWithdrawAmt(parseInt(e.target.value) || 0)}
            disabled={!isLeader}
            className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1 text-sm text-[#E2DFD8] disabled:opacity-30"
          />
          <button
            data-testid="vault-withdraw"
            onClick={withdraw}
            disabled={busy || !isLeader || withdrawAmt <= 0 || vault.gold < withdrawAmt}
            className="w-full py-1 border border-[#333] text-[#8c8c8c] hover:border-[#E5C34A] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30"
          >
            {isLeader ? "Withdraw" : "Leader Only"}
          </button>
        </div>
      </div>

      {/* Items */}
      <div className="rune-div" />
      <div>
        <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1">
          Vault Items ({(vault.items || []).length})
        </div>
        {(vault.items || []).length === 0 && (
          <p className="italic text-[10px] text-[#666]">Empty shelves.</p>
        )}
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {(vault.items || []).map(item => (
            <div key={item.id} className="flex justify-between items-center border border-[#262626] p-1" data-testid={`vault-item-${item.id}`}>
              <div className="text-xs">
                <span className={`rarity-${item.rarity}`}>{item.name}</span>
                <span className="text-[#666] text-[10px] ml-1">({item.rarity})</span>
              </div>
              {isLeader && (
                <button
                  data-testid={`vault-take-${item.id}`}
                  onClick={() => withdrawItem(item.id)}
                  disabled={busy}
                  className="px-2 py-0.5 text-[10px] font-heading tracking-widest uppercase border border-[#333] text-[#8c8c8c] hover:border-[#B89C3A]"
                >Take</button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-1 mt-1">
          <select
            data-testid="vault-item-select"
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            className="flex-1 bg-[#0a0a0a] border border-[#262626] px-2 py-1 text-xs text-[#E2DFD8]"
          >
            <option value="">— deposit from pack —</option>
            {(ch.inventory || []).map(i => (
              <option key={i.id} value={i.id}>{i.name} ({i.rarity})</option>
            ))}
          </select>
          <button
            data-testid="vault-deposit-item"
            onClick={depositItem}
            disabled={busy || !selectedItem}
            className="px-2 py-1 border border-[#4A5D23] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30"
          >
            Deposit
          </button>
        </div>
      </div>

      {isLeader && (
        <>
          <div className="rune-div" />
          <button
            data-testid="guild-upgrade"
            onClick={upgrade}
            disabled={busy || vault.gold < upgradeCost}
            className="w-full py-2 border border-[#E55A00] text-[#E55A00] font-heading text-xs tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-30"
          >
            <ArrowUp className="w-3 h-3" strokeWidth={2} />
            Upgrade Guild to Lv {guild.level + 1} ({upgradeCost}g)
          </button>
          <p className="text-[10px] italic text-[#666] text-center">
            +2 max energy for every member per level.
          </p>
        </>
      )}
    </div>
  );
}
